import React, { useState } from 'react'
import type { ContactFormValues } from '@/types'

const consultationTypes = [
  'Consultas',
  'Medicina preventiva',
  'Atención por especie',
  'Cirugía',
  'Internación y cuidados',
  'Enfermería',
  'Laboratorio básico',
  'Dermatología',
  'Odontología',
  'Producción animal',
  'Bromatología e inocuidad alimentaria',
  'Bienestar y asesoramiento',
]

const animalSpecies = ['perro', 'gato', 'bovino', 'equino', 'ovino', 'caprino', 'porcino', 'ave']

interface ContactFormProps {
  onSubmit?: (data: ContactFormValues) => void
  isSubmitting?: boolean
  submitError?: string
}

function ContactForm({ onSubmit, isSubmitting = false, submitError }: ContactFormProps) {
  const [formData, setFormData] = useState<ContactFormValues>({
    full_name: '',
    email: '',
    phone: '',
    pet_name: '',
    animal_species: '',
    pet_age: '',
    consultation_type: '',
    description: '',
    privacy_accepted: false,
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.full_name.trim()) {
      newErrors.full_name = 'El nombre es requerido'
    } else if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]{2,100}$/.test(formData.full_name.trim())) {
      newErrors.full_name = 'El nombre debe contener solo letras y espacios (2-100 caracteres)'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido'
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email) || formData.email.length > 100) {
      newErrors.email = 'Email inválido (máximo 100 caracteres)'
    }

    if (!formData.pet_name.trim()) {
      newErrors.pet_name = 'El nombre de la mascota es requerido'
    } else if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]{1,100}$/.test(formData.pet_name.trim())) {
      newErrors.pet_name = 'El nombre debe contener solo letras y espacios (1-100 caracteres)'
    }

    if (!formData.animal_species) {
      newErrors.animal_species = 'La especie es requerida'
    }

    if (!formData.pet_age.trim()) {
      newErrors.pet_age = 'La edad es requerida'
    } else if (formData.pet_age.length > 20) {
      newErrors.pet_age = 'La edad no puede superar los 20 caracteres'
    } else if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s\d]{1,20}$/.test(formData.pet_age.trim())) {
      newErrors.pet_age = 'La edad contiene caracteres inválidos'
    }

    if (!formData.consultation_type) {
      newErrors.consultation_type = 'El tipo de consulta es requerido'
    }

    if (!formData.description.trim()) {
      newErrors.description = 'La descripción es requerida'
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'La descripción debe tener al menos 10 caracteres'
    } else if (formData.description.length > 2000) {
      newErrors.description = 'La descripción no puede superar los 2000 caracteres'
    }

    if (!formData.privacy_accepted) {
      newErrors.privacy_accepted = 'Debe aceptar la política de privacidad'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }))
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    try {
      await onSubmit?.(formData)
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        pet_name: '',
        animal_species: '',
        pet_age: '',
        consultation_type: '',
        description: '',
        privacy_accepted: false,
      })
      setErrors({})
    } catch (error) {
      // Error is handled by the parent component
    }
  }

  return (
    <div className="glass-card rounded-lg p-6 shadow-xl border border-gray-200/60">
      <h2 className="text-2xl font-bold text-text-primary mb-6">Envíanos un mensaje</h2>

      {submitError && <div className="mb-4 p-3 bg-error/10 text-error rounded">{submitError}</div>}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <div className="relative">
            <i className="ph ph-user absolute left-3 top-1/2 -translate-y-1/2 text-xl text-secondary"></i>
        <input
          type="text"
          name="full_name"
          placeholder="Nombre y apellido"
          value={formData.full_name}
          onChange={handleChange}
          maxLength={100}
          className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:border-primary focus:ring-1 focus:ring-primary transition ${
            errors.full_name ? 'border-error' : 'border-gray-300'
          }`}
        />
          </div>
          {errors.full_name && <p className="text-sm text-error mt-1">{errors.full_name}</p>}
        </div>

        <div>
          <div className="relative">
            <i className="ph ph-envelope absolute left-3 top-1/2 -translate-y-1/2 text-xl text-secondary"></i>
        <input
          type="email"
          name="email"
          placeholder="Email"
          value={formData.email}
          onChange={handleChange}
          maxLength={100}
          className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:border-primary focus:ring-1 focus:ring-primary transition ${
            errors.email ? 'border-error' : 'border-gray-300'
          }`}
        />
          </div>
          {errors.email && <p className="text-sm text-error mt-1">{errors.email}</p>}
        </div>

        <div>
          <div className="relative">
            <i className="ph ph-paw-print absolute left-3 top-1/2 -translate-y-1/2 text-xl text-secondary"></i>
        <input
          type="text"
          name="pet_name"
          placeholder="Nombre de la mascota"
          value={formData.pet_name}
          onChange={handleChange}
          maxLength={100}
          className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:border-primary focus:ring-1 focus:ring-primary transition ${
            errors.pet_name ? 'border-error' : 'border-gray-300'
          }`}
        />
          </div>
          {errors.pet_name && <p className="text-sm text-error mt-1">{errors.pet_name}</p>}
        </div>

        <div>
          <div className="relative">
            <i className="ph ph-horse absolute left-3 top-1/2 -translate-y-1/2 text-xl text-secondary"></i>
            <select
              name="animal_species"
              value={formData.animal_species}
              onChange={handleChange}
              className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:border-primary focus:ring-1 focus:ring-primary transition ${
                errors.animal_species ? 'border-error' : 'border-gray-300'
              }`}
            >
              <option value="">Especie animal</option>
              {animalSpecies.map((species) => (
                <option key={species} value={species}>
                  {species}
                </option>
              ))}
            </select>
          </div>
          {errors.animal_species && <p className="text-sm text-error mt-1">{errors.animal_species}</p>}
        </div>

        <div>
          <div className="relative">
            <i className="ph ph-calendar absolute left-3 top-1/2 -translate-y-1/2 text-xl text-secondary"></i>
        <input
          type="text"
          name="pet_age"
          placeholder="Edad de la mascota (ej: 3 años)"
          value={formData.pet_age}
          onChange={handleChange}
          maxLength={20}
          className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:border-primary focus:ring-1 focus:ring-primary transition ${
            errors.pet_age ? 'border-error' : 'border-gray-300'
          }`}
        />
          </div>
          {errors.pet_age && <p className="text-sm text-error mt-1">{errors.pet_age}</p>}
        </div>

        <div>
          <div className="relative">
            <i className="ph ph-stethoscope absolute left-3 top-1/2 -translate-y-1/2 text-xl text-secondary"></i>
            <select
              name="consultation_type"
              value={formData.consultation_type}
              onChange={handleChange}
              className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:border-primary focus:ring-1 focus:ring-primary transition ${
                errors.consultation_type ? 'border-error' : 'border-gray-300'
              }`}
            >
              <option value="">Tipo de consulta</option>
              {consultationTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          {errors.consultation_type && <p className="text-sm text-error mt-1">{errors.consultation_type}</p>}
        </div>

        <div>
          <div className="relative">
            <i className="ph ph-note absolute left-3 top-1/2 -translate-y-1/2 text-xl text-secondary"></i>
        <textarea
          name="description"
          placeholder="Descripción del caso / Comentario adicional"
          value={formData.description}
          onChange={handleChange}
          maxLength={2000}
          rows={4}
          className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:border-primary focus:ring-1 focus:ring-primary transition resize-y ${
            errors.description ? 'border-error' : 'border-gray-300'
          }`}
        />
        <p className="text-xs text-text-secondary mt-1">{formData.description.length}/2000 caracteres (mínimo 10)</p>
          </div>
          {errors.description && <p className="text-sm text-error mt-1">{errors.description}</p>}
        </div>

        <label className="flex items-start space-x-3">
          <input
            type="checkbox"
            name="privacy_accepted"
            checked={formData.privacy_accepted}
            onChange={handleChange}
            className="mt-1 rounded border-gray-300 text-primary focus:ring-primary"
          />
          <span className="text-sm text-text-secondary">
            Acepto la{' '}
            <a href="#" className="text-primary hover:underline">
              política de privacidad
            </a>{' '}
            y los términos del sitio.
          </span>
        </label>
        {errors.privacy_accepted && <p className="text-sm text-error mt-1">{errors.privacy_accepted}</p>}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-primary text-white py-3 rounded-lg font-medium hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isSubmitting ? (
            <>
              <i className="ph ph-spinner animate-spin mr-2"></i>
              Enviando...
            </>
          ) : (
            'Enviar mensaje'
          )}
        </button>
      </form>
    </div>
  )
}

export default ContactForm